import type { Column } from "@tanstack/react-table";
import { ChevronDown, ChevronRight, Filter as FilterIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DocumentVersionSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { cn } from "~/lib/utils";
import SearchBar from "~/molecules/SearchBar";
import type { ResolvedDocumentsColumnGroup } from "./columnRegistry";

interface ToolbarProps {
  isFiltersOpen: boolean;
  onToggleFilters: () => void;
  titleSearch: string;
  onTitleSearchChange: (value: string) => void;
  titlePlaceholder: string;
  columnsButtonLabel: string;
  ungroupedColumns: Column<DocumentVersionSummary, unknown>[];
  resolvedGroups: ResolvedDocumentsColumnGroup[];
  columnMenuLabels: Record<string, string>;
}

export const Toolbar = ({
  isFiltersOpen,
  onToggleFilters,
  titleSearch,
  onTitleSearchChange,
  titlePlaceholder,
  columnsButtonLabel,
  ungroupedColumns,
  resolvedGroups,
  columnMenuLabels,
}: ToolbarProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" onClick={onToggleFilters} className="shrink-0">
        <FilterIcon className="h-4 w-4" />
        {t(($) => $.commons.filter.title)}
        <ChevronRight
          className={cn(
            "h-4 w-4 transition-transform",
            isFiltersOpen && "rotate-90",
          )}
        />
      </Button>
      <SearchBar
        placeholder={titlePlaceholder}
        value={titleSearch}
        onSearch={onTitleSearchChange}
        onDebouncedSearch={onTitleSearchChange}
        onClearSearch={() => onTitleSearchChange("")}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="ml-auto shrink-0">
            {columnsButtonLabel} <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {ungroupedColumns.map((column) => (
            <DropdownMenuCheckboxItem
              key={column.id}
              className="capitalize"
              checked={column.getIsVisible()}
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(value) => column.toggleVisibility(!!value)}
            >
              {columnMenuLabels[column.id]}
            </DropdownMenuCheckboxItem>
          ))}

          {resolvedGroups.length > 0 && <DropdownMenuSeparator />}
          {resolvedGroups.map((group, index) => {
            const visibleColumns = group.columns.filter((column) =>
              column.getIsVisible(),
            ).length;
            const groupChecked =
              visibleColumns === 0
                ? false
                : visibleColumns === group.columns.length
                  ? true
                  : "indeterminate";

            return (
              <div key={group.id}>
                {index > 0 && <DropdownMenuSeparator />}
                <DropdownMenuCheckboxItem
                  checked={groupChecked}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(value) => {
                    const shouldShow = value === true;
                    group.columns.forEach((column) =>
                      column.toggleVisibility(shouldShow),
                    );
                  }}
                >
                  {group.label}
                </DropdownMenuCheckboxItem>
                {group.columns.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="text-muted-foreground pl-11"
                    checked={column.getIsVisible()}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {columnMenuLabels[column.id]}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
